import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DraftsIcon from '@mui/icons-material/Drafts';
// MainLayout sudah disediakan oleh MainLayoutWrapper di App.js
import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { t } from '../../utils/textUtils';

// Import form steps
import GeneralInfoForm from './forms/GeneralInfoForm';
import ScheduleForm from './forms/ScheduleForm';
import WorkPlanForm from './forms/WorkPlanForm';
import BudgetForm from './forms/BudgetForm';
import TeamForm from './forms/TeamForm';
import ReviewForm from './forms/ReviewForm';

const steps = [
  t('INFORMASI UMUM'),
  t('WAKTU & JADWAL'),
  t('RENCANA & VOLUME'),
  t('BIAYA & PENDANAAN'),
  t('TIM PELAKSANA'),
  t('REVIEW')
];

const CreateProject = () => {
  const { user } = useAuth(); // Hanya menggunakan user, tidak perlu token
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [dropdownData, setDropdownData] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [autosaveTimer, setAutosaveTimer] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Form data state
  const [formData, setFormData] = useState({
    // Informasi Umum Proyek
    name: '',
    noPO: '',
    kodeProyek: '',
    location: '',
    latitude: '',
    longitude: '',
    jenisProyek: '',
    sektor: '',
    description: '',
    
    // Waktu dan Jadwal
    startDate: null,
    estimatedEndDate: null,
    
    // Rencana & Volume Pekerjaan
    jumlahUnitBangunan: '',
    jumlahUnitModular: '', // Keep for backward compatibility
    tipeBangunan: '',
    tipePrefabrikasi: '', // Keep for backward compatibility
    luasTotalLahan: '',
    luasTotalBangunan: '',
    layoutDesainFile: null,
    
    // Biaya dan Pendanaan
    totalAnggaran: '',
    sumberDana: '',
    rencanaPembayaran: '',
    
    // Tim Pelaksana
    pemilikProyek: '',
    managerId: '',
    productionCoordinatorId: '', // Changed from koordinatorProduksiPabrik
    siteManagerId: '',
    siteEngineerIds: [],
    installationTeamId: '', // Changed from timInstalasiPemasangan
    
    // Mandor per unit
    foremen: [],
    
    // Other fields
    projectClientId: '', // Changed from clientId
    status: 'planning',
  });
  
  // Validation state for each step
  const [validationErrors, setValidationErrors] = useState({
    step0: {},
    step1: {},
    step2: {},
    step3: {},
    step4: {},
  });

  // Load draft from localStorage on component mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('projectDraft');
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        setFormData(parsedDraft.formData);
        setLastSaved(new Date(parsedDraft.timestamp));
        enqueueSnackbar(t('DRAFT PROYEK BERHASIL DIMUAT'), { variant: 'info' });
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [enqueueSnackbar]);
  
  // Setup autosave timer
  useEffect(() => {
    // Clear previous timer
    if (autosaveTimer) {
      clearTimeout(autosaveTimer);
    }
    
    // Set new timer to save draft every 30 seconds
    const timer = setTimeout(() => {
      saveDraft();
    }, 30000); // 30 seconds
    
    setAutosaveTimer(timer);
    
    // Cleanup on unmount
    return () => {
      if (autosaveTimer) {
        clearTimeout(autosaveTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);
  
  // Fetch users for team selection
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const response = await api.get('/projects/dropdown-data');
        if (response.data) {
          setDropdownData(response.data);
          
          // Combine all users into one array for ReviewForm
          const allUsers = [
            ...(response.data.projectManagers || []),
            ...(response.data.siteManagers || []),
            ...(response.data.siteEngineers || []),
            ...(response.data.clients || []),
            ...(response.data.productionCoordinators || []),
            ...(response.data.installationTeams || [])
          ];
          setUsers(allUsers);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        enqueueSnackbar(t('GAGAL MEMUAT DATA DROPDOWN'), { variant: 'error' });
      }
    };
    
    fetchDropdownData();
  }, [enqueueSnackbar]);

  // Handle form data changes
  const handleChange = (newData) => {
    setFormData(prev => ({
      ...prev,
      ...newData
    }));
  };
  
  // Save draft to localStorage
  const saveDraft = () => {
    const draft = {
      formData,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('projectDraft', JSON.stringify(draft));
    setLastSaved(new Date());
  };
  
  // Clear draft data
  // eslint-disable-next-line no-unused-vars
  const clearDraft = () => {
    localStorage.removeItem('projectDraft');
    setLastSaved(null);
  };

  // Validate current step
  const validateStep = (step) => {
    let errors = {};
    let isValid = true;
    
    switch (step) {
      case 0: // General Info
        if (!formData.name) {
          errors.name = t('NAMA PROYEK WAJIB DIISI');
          isValid = false;
        }
        if (!formData.location) {
          errors.location = t('LOKASI PROYEK WAJIB DIISI');
          isValid = false;
        }
        if (!formData.jenisProyek) {
          errors.jenisProyek = t('JENIS PROYEK WAJIB DIISI');
          isValid = false;
        }
        break;
        
      case 1: // Schedule
        if (!formData.startDate) {
          errors.startDate = t('TANGGAL MULAI WAJIB DIISI');
          isValid = false;
        }
        if (!formData.estimatedEndDate) {
          errors.estimatedEndDate = t('TANGGAL TARGET SELESAI WAJIB DIISI');
          isValid = false;
        }
        if (formData.startDate && formData.estimatedEndDate && 
            new Date(formData.startDate) >= new Date(formData.estimatedEndDate)) {
          errors.estimatedEndDate = t('TANGGAL TARGET SELESAI HARUS SETELAH TANGGAL MULAI');
          isValid = false;
        }
        break;
        
      case 2: // Work Plan
        if (formData.jumlahUnitModular && isNaN(formData.jumlahUnitModular)) {
          errors.jumlahUnitModular = t('JUMLAH UNIT HARUS BERUPA ANGKA');
          isValid = false;
        }
        if (formData.luasTotalLahan && isNaN(formData.luasTotalLahan)) {
          errors.luasTotalLahan = t('LUAS LAHAN HARUS BERUPA ANGKA');
          isValid = false;
        }
        if (formData.luasTotalBangunan && isNaN(formData.luasTotalBangunan)) {
          errors.luasTotalBangunan = t('LUAS BANGUNAN HARUS BERUPA ANGKA');
          isValid = false;
        }
        break;
        
      case 3: // Budget
        if (formData.totalAnggaran && isNaN(formData.totalAnggaran)) {
          errors.totalAnggaran = t('TOTAL ANGGARAN HARUS BERUPA ANGKA');
          isValid = false;
        }
        break;
        
      case 4: // Team
        // Optional validation for team members
        break;
        
      default:
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [`step${step}`]: errors
    }));
    
    return isValid;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prevStep => prevStep + 1);
    }
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  // Open confirmation dialog
  const handleOpenConfirmDialog = () => {
    if (validateAllSteps()) {
      setOpenConfirmDialog(true);
    }
  };

  // Close confirmation dialog
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };
  
  // Validate all steps before submission
  const validateAllSteps = () => {
    // Validate required fields
    if (!formData.name) {
      setError(t('NAMA PROYEK WAJIB DIISI'));
      setActiveStep(0); // Go back to general info step
      return false;
    }
    
    if (!formData.startDate || !formData.estimatedEndDate) {
      setError(t('TANGGAL MULAI DAN TANGGAL TARGET SELESAI WAJIB DIISI'));
      setActiveStep(1); // Go to schedule step
      return false;
    }
    
    if (!formData.managerId) {
      setError(t('MANAGER PROYEK WAJIB DIPILIH'));
      setActiveStep(4); // Go to team step
      return false;
    }
    
    if (!formData.projectClientId) {
      setError(t('CLIENT WAJIB DIPILIH'));
      setActiveStep(4); // Go to team step
      return false;
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare form data for API
      const projectData = {
        ...formData,
        // Convert dates to ISO format
        startDate: formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : null,
        estimatedEndDate: formData.estimatedEndDate ? new Date(formData.estimatedEndDate).toISOString().split('T')[0] : null,
        
        // Convert numeric fields
        jumlahUnitModular: formData.jumlahUnitModular ? parseInt(formData.jumlahUnitModular, 10) : null,
        luasTotalLahan: formData.luasTotalLahan ? parseFloat(formData.luasTotalLahan) : null,
        luasTotalBangunan: formData.luasTotalBangunan ? parseFloat(formData.luasTotalBangunan) : null,
        totalAnggaran: formData.totalAnggaran ? parseFloat(formData.totalAnggaran) : null,
        
        // Set current user as manager if not specified
        managerId: formData.managerId || user.id,
        siteEngineerIds: Array.isArray(formData.siteEngineerIds)
          ? formData.siteEngineerIds.filter(id => id !== null && id !== undefined && id !== '')
          : [],
        
        // Kirim installationTeamId sebagai null agar tidak gagal validasi
        installationTeamId: null,
        // Kirim foremen dengan unitNumber yang valid untuk menghindari error not-null constraint
        foremen: [{ 
          name: 'Default Mandor',
          unitNumber: 1 // Nilai default untuk unitNumber yang wajib
        }],
      };
      
      // Handle file upload separately if needed
      // ...
      
      // Create project
      const response = await api.post('/projects', projectData);
      
      if (response.data && response.data.project) {
        // Clear draft after successful submission
        localStorage.removeItem('projectDraft');
        setLastSaved(null);
        setFormData({
          // Reset form data to default values
          name: '',
          kodeProyek: '',
          location: '',
          jenisProyek: '',
          description: '',
          startDate: null,
          estimatedEndDate: null,
          jumlahUnitModular: '',
          tipePrefabrikasi: '',
          luasTotalLahan: '',
          luasTotalBangunan: '',
          layoutDesainFile: null,
          totalAnggaran: '',
          sumberDana: '',
          rencanaPembayaran: '',
          pemilikProyek: '',
          managerId: '',
          productionCoordinatorId: '',
          siteManagerId: '',
          siteEngineerIds: [],
          installationTeamId: '',
          foremen: [],
          projectClientId: '',
          status: 'planning',
        });
        enqueueSnackbar(t('PROYEK BERHASIL DIBUAT!'), { variant: 'success' });
        navigate('/projects');
      } else {
        throw new Error(t('GAGAL MEMBUAT PROYEK: RESPONS TIDAK VALID'));
      }
      
      // Close dialog
      handleCloseConfirmDialog();
      
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error.response?.data?.message || t('TERJADI KESALAHAN SAAT MEMBUAT PROYEK'));
      enqueueSnackbar(t('GAGAL MEMBUAT PROYEK'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Render the current step form
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <GeneralInfoForm 
            formData={formData} 
            onChange={handleChange} 
            errors={validationErrors.step0} 
          />
        );
      case 1:
        return (
          <ScheduleForm 
            formData={formData} 
            onChange={handleChange} 
            errors={validationErrors.step1} 
          />
        );
      case 2:
        return (
          <WorkPlanForm 
            formData={formData} 
            onChange={handleChange} 
            errors={validationErrors.step2} 
          />
        );
      case 3:
        return (
          <BudgetForm 
            formData={formData} 
            onChange={handleChange} 
            errors={validationErrors.step3} 
          />
        );
      case 4:
        return (
          <TeamForm 
            formData={formData} 
            onChange={handleChange} 
            errors={validationErrors.step4}
            dropdownData={dropdownData}
          />
        );
      case 5:
        return (
          <ReviewForm 
            formData={formData} 
            users={users}
            dropdownData={dropdownData}
          />
        );
      default:
        return null;
    }
  };

  return (
      <Box sx={{ width: '100%' }}>
        {/* Header with blue gradient */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #0f172a, #1e3a5f, #1e40af)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '40%',
              height: '100%',
              background: 'radial-gradient(circle at top right, rgba(59, 130, 246, 0.2), transparent 70%)',
              pointerEvents: 'none'
            }
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" position="relative" zIndex={1}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff', mb: 1 }}>
                {t('BUAT PROYEK BARU')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                {t('ISI FORMULIR BERIKUT UNTUK MEMBUAT PROYEK KONSTRUKSI PREFABRIKASI BARU')}
              </Typography>
            </Box>
            {lastSaved && (
              <Chip
                icon={<DraftsIcon fontSize="small" />}
                label={`${t('DRAFT TERAKHIR')}: ${lastSaved.toLocaleTimeString()}`}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600
                }}
              />
            )}
          </Stack>
        </Paper>
        
        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.1)',
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
            mb: 3
          }}
        >
          {/* Stepper */}
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'rgba(15, 23, 42, 0.1)',
              bgcolor: '#F8FAFC',
              overflow: 'hidden'
            }}
          >
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel 
              sx={{ 
                py: 2.5, 
                px: { xs: 1, sm: 2 },
                '& .MuiStepLabel-label': {
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#64748B',
                  '&.Mui-active': {
                    color: '#1e40af',
                    fontWeight: 700
                  },
                  '&.Mui-completed': {
                    color: '#059669',
                    fontWeight: 600
                  }
                },
                '& .MuiStepIcon-root': {
                  color: '#CBD5E1',
                  '&.Mui-active': {
                    color: '#3B82F6'
                  },
                  '&.Mui-completed': {
                    color: '#059669'
                  }
                },
                '& .MuiStepConnector-line': {
                  borderColor: '#E2E8F0'
                },
                '& .MuiStepConnector-active': {
                  '& .MuiStepConnector-line': {
                    borderColor: '#3B82F6'
                  }
                }
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                border: '1px solid rgba(239, 68, 68, 0.2)',
                bgcolor: 'rgba(254, 226, 226, 0.5)'
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form Content */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'rgba(15, 23, 42, 0.1)',
              bgcolor: '#FFFFFF',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              mb: 4
            }}
          >
            {renderStepContent(activeStep)}
          </Paper>

          {/* Action Buttons */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'rgba(15, 23, 42, 0.1)',
              bgcolor: '#F8FAFC',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={activeStep === 0 ? () => navigate('/projects') : handleBack}
                sx={{ 
                  borderColor: 'rgba(15, 23, 42, 0.2)',
                  color: '#0f172a',
                  fontWeight: 600,
                  '&:hover': { 
                    backgroundColor: 'rgba(59, 130, 246, 0.04)',
                    borderColor: 'rgba(59, 130, 246, 0.4)'
                  }
                }}
              >
                {activeStep === 0 ? t('KEMBALI') : t('SEBELUMNYA')}
              </Button>
              
              <Stack direction="row" spacing={2}>
                {activeStep === steps.length - 1 && (
                  <Button
                    variant="outlined"
                    startIcon={<DraftsIcon />}
                    onClick={saveDraft}
                    sx={{ 
                      borderColor: 'rgba(15, 23, 42, 0.2)',
                      color: '#0f172a',
                      fontWeight: 600,
                      '&:hover': { 
                        backgroundColor: 'rgba(59, 130, 246, 0.04)',
                        borderColor: 'rgba(59, 130, 246, 0.4)'
                      }
                    }}
                  >
                    {t('SIMPAN DRAFT')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  endIcon={activeStep === steps.length - 1 ? <SaveIcon /> : null}
                  onClick={activeStep === steps.length - 1 ? handleOpenConfirmDialog : handleNext}
                  disabled={loading}
                  sx={{ 
                    bgcolor: 'linear-gradient(135deg, #3B82F6, #1e40af)',
                    '&:hover': { bgcolor: 'linear-gradient(135deg, #2563EB, #1e3a8a)' },
                    color: '#ffffff',
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    textTransform: 'none',
                    px: 3
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                  ) : activeStep === steps.length - 1 ? (
                    t('BUAT PROYEK')
                  ) : (
                    t('SELANJUTNYA')
                  )}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Paper>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(15, 23, 42, 0.15)'
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ 
          backgroundColor: 'linear-gradient(135deg, #0f172a, #1e3a5f)',
          color: '#ffffff',
          fontWeight: 800,
          py: 2.5,
          px: 3
        }}>
          {t('KONFIRMASI PEMBUATAN PROYEK')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText id="alert-dialog-description" sx={{ color: '#475569', fontSize: '1rem' }}>
            {t('APAKAH ANDA YAKIN INGIN MEMBUAT PROYEK BARU BERNAMA')} <strong style={{ color: '#0f172a' }}>{formData.name}</strong>? 
          </DialogContentText>
          <DialogContentText sx={{ color: '#64748B', mt: 2, fontSize: '0.9rem' }}>
            {t('DATA YANG TELAH ANDA MASUKKAN AKAN DISIMPAN DAN TIDAK DAPAT DIUBAH KECUALI MELALUI FITUR EDIT PROYEK.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(15, 23, 42, 0.1)', gap: 1 }}>
          <Button 
            onClick={handleCloseConfirmDialog}
            sx={{ 
              color: '#64748B',
              fontWeight: 600,
              '&:hover': { 
                backgroundColor: 'rgba(15, 23, 42, 0.04)'
              }
            }}
          >
            {t('BATAL')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            autoFocus 
            variant="contained"
            disabled={loading}
            sx={{ 
              bgcolor: 'linear-gradient(135deg, #3B82F6, #1e40af)',
              '&:hover': { bgcolor: 'linear-gradient(135deg, #2563EB, #1e3a8a)' },
              color: '#ffffff',
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
              px: 3
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
            ) : (
              t('YA, BUAT PROYEK')
            )}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
  );
};

export default CreateProject;
